// Scene-side lifecycle for the Stage 5 selection registry.
//
// `syncSelectionWireframes(viewer, params)` is the workhorse — diffs
// `params.selections` against `viewer.objects.selections.wireframes` and
// adds/updates/removes meshes idempotently. Called from the
// `selection:*` intent handlers after each mutation. Materials are shared
// across selections (one box, one sphere, one accent for the focused one).
//
// Coords: wireframe geometry is built in Three.js space via
// `picking/geometry.ts` helpers (which call `physicalToThreeJS`). No need
// to apply additional mesh-level transforms here.

import * as THREE from 'three';
import { createBoxWireframe, createSphereWireframe } from './picking/geometry';
import type { ViewerContext } from '../app/context';
import type { ParamsState, Selection, SelectionId } from '../store/schema';

/**
 * Lazy-init the shared materials + parent group on the viewer. Idempotent —
 * safe to call before each sync. Theme colors are pulled at this point;
 * future theme-apply hooks can recolor via `applyTheme`.
 */
function makeWireMat(color: string, opacity: number): THREE.LineBasicMaterial {
    return new THREE.LineBasicMaterial({
        color: new THREE.Color(color),
        transparent: true,
        opacity,
        depthWrite: false,
    });
}

export function ensureSelectionsScene(viewer: ViewerContext): void {
    const obj = viewer.objects.selections;
    const scene = viewer.three.scene;
    if (!scene) return;

    const gridColor = viewer.theme.current?.scene?.grid?.color ?? '#888888';
    const accentColor = viewer.theme.current?.ui?.accent ?? '#ffaa44';

    if (!obj.boxMaterial)    obj.boxMaterial    = makeWireMat(gridColor, 0.45);
    if (!obj.sphereMaterial) obj.sphereMaterial = makeWireMat(gridColor, 0.45);
    if (!obj.accentMaterial) obj.accentMaterial = makeWireMat(accentColor, 0.85);
    if (!obj.group) {
        obj.group = new THREE.Group();
        obj.group.name = 'SelectionRegistry';
        scene.add(obj.group);
    }
}

function materialFor(viewer: ViewerContext, sel: Selection, focused: boolean): THREE.LineBasicMaterial {
    const obj = viewer.objects.selections;
    if (focused && obj.accentMaterial) return obj.accentMaterial;
    if (sel.kind === 'box') return obj.boxMaterial!;
    return obj.sphereMaterial!;
}

function buildWireframe(viewer: ViewerContext, sel: Selection, focused: boolean): THREE.LineSegments {
    const mat = materialFor(viewer, sel, focused);
    const mesh = sel.kind === 'box'
        ? createBoxWireframe(sel, mat)
        : createSphereWireframe(sel, mat);
    mesh.userData['geomKey'] = geomKey(sel);
    return mesh;
}

/** Identity of the geometry-relevant fields. When this doesn't change,
 *  `syncSelectionWireframes` skips the dispose+rebuild and only updates
 *  the material reference. */
function geomKey(sel: Selection): string {
    if (sel.kind === 'box') {
        const [lx, ly, lz] = sel.lower;
        const [ux, uy, uz] = sel.upper;
        return `box:${lx}:${ly}:${lz}:${ux}:${uy}:${uz}`;
    }
    const [cx, cy, cz] = sel.center;
    return `sphere:${cx}:${cy}:${cz}:${sel.radius}`;
}

function disposeWireframe(mesh: THREE.LineSegments | undefined): void {
    if (!mesh) return;
    if (mesh.parent) mesh.parent.remove(mesh);
    mesh.geometry.dispose();
}

/**
 * Sync the wireframe meshes to mirror `params.selections`. Idempotent.
 * On `focusedSelectionId` change, swaps the focused mesh onto the accent
 * material (and the previously-focused mesh back onto its kind material).
 */
export function syncSelectionWireframes(viewer: ViewerContext, params: ParamsState): void {
    ensureSelectionsScene(viewer);
    const obj = viewer.objects.selections;
    const group = obj.group;
    if (!group) return;

    const wanted = new Set<SelectionId>(params.selectionOrder);
    const focused = params.focusedSelectionId;

    // Remove any wireframe whose id is no longer in the registry.
    for (const [id, mesh] of obj.wireframes) {
        if (!wanted.has(id)) {
            disposeWireframe(mesh);
            obj.wireframes.delete(id);
        }
    }

    // Add / update wireframes for every registered selection.
    for (const id of params.selectionOrder) {
        const sel = params.selections[id];
        if (!sel) continue;
        const existing = obj.wireframes.get(id);
        if (!existing) {
            const mesh = buildWireframe(viewer, sel, focused === id);
            group.add(mesh);
            obj.wireframes.set(id, mesh);
            continue;
        }
        // Material swap is always cheap (reference compare); rebuild geometry
        // only when the shape-relevant fields changed.
        existing.material = materialFor(viewer, sel, focused === id);
        const key = geomKey(sel);
        if (existing.userData['geomKey'] === key) continue;
        existing.geometry.dispose();
        const fresh = buildWireframe(viewer, sel, focused === id);
        existing.geometry = fresh.geometry;
        existing.position.copy(fresh.position);
        existing.userData['geomKey'] = key;
    }
}

/** Remove a single wireframe and dispose its geometry. */
export function removeSelectionWireframe(viewer: ViewerContext, id: SelectionId): void {
    const obj = viewer.objects.selections;
    const mesh = obj.wireframes.get(id);
    if (!mesh) return;
    disposeWireframe(mesh);
    obj.wireframes.delete(id);
}

/** Dispose every wireframe + shared material. Called on scene teardown / dataset switch. */
export function disposeAllSelectionWireframes(viewer: ViewerContext): void {
    const obj = viewer.objects.selections;
    for (const [, mesh] of obj.wireframes) disposeWireframe(mesh);
    obj.wireframes.clear();
    if (obj.boxMaterial)    { obj.boxMaterial.dispose();    obj.boxMaterial = null; }
    if (obj.sphereMaterial) { obj.sphereMaterial.dispose(); obj.sphereMaterial = null; }
    if (obj.accentMaterial) { obj.accentMaterial.dispose(); obj.accentMaterial = null; }
    if (obj.group) {
        if (obj.group.parent) obj.group.parent.remove(obj.group);
        obj.group = null;
    }
}

/** Recolor the shared materials from the current theme. Called on theme apply. */
export function applyThemeToSelectionWireframes(viewer: ViewerContext): void {
    const obj = viewer.objects.selections;
    const gridColor = viewer.theme.current?.scene?.grid?.color;
    const accentColor = viewer.theme.current?.ui?.accent;
    if (gridColor) {
        obj.boxMaterial?.color.set(gridColor);
        obj.sphereMaterial?.color.set(gridColor);
    }
    if (accentColor) {
        obj.accentMaterial?.color.set(accentColor);
    }
}
